import React, { useState, useEffect } from 'react';
import * as _ from 'lodash';
import PropTypes from 'prop-types';
import ChevronDown from '@fabric-ds/icons/dist/16/chevron-down.svg';
import MinusIcon from '@fabric-ds/icons/dist/16/minus.svg';
import LinkIcon from '@fabric-ds/icons/dist/16/link-external.svg';
import { useDeviceSize } from 'common/lib/hooks/useDeviceSize';
import { formatPhoneNumber } from 'common/lib/helpers/TextUtils';
import { AD_TYPE_KFS } from 'common/lib/helpers/constants';
import {
    eventCollectorProspectUrlClick,
    trackClickOnLinksInUsefulLinks,
    eventCollectorProjectUrlClick,
    trackEventOrderProspect,
    trackGoToHomepage,
    trackSendMessage,
    trackClickOnCallContact,
    trackClickOnExtendedProfileCta,
    trackClickOnExtendedProfileLink,
} from 'common/lib/helpers/trackingService';
import { Expandable } from '@fabric-ds/react';
import * as S from './styles';
import { get } from '../../helpers/Rest';

const CompanyProfile = props => {
    const { expandable, data } = props;

    const {
        orgId,
        finnkode,
        brokers,
        projectUrl,
        developers,
        financialAgents,
        orderUrl,
        imageHost,
        adType,
        moreInfoLinks,
        apiHost,
        pictureUrl,
        leisure,
    } = data;

    const [companyProfileData, setCompanyProfileData] = useState({});

    useEffect(() => {
        get(
            `${apiHost}org/profile/company-provider/${finnkode}`,
            response => setCompanyProfileData(response),
            () => {}
        );
    }, []);

    const {
        mainTextColor,
        mainBackgroundColor,
        logoBackgroundColor,
        sloganTextColor,
        sloganBackgroundColor,
        logoUrl,
        orgDisplayName,
        slogan,
        ctaButton,
        ctaButtonColor,
        customLinks,
    } = companyProfileData;

    const [expanded, setExpanded] = useState(false);
    const isExpanded = expanded || !expandable;

    // Logo is unavailable from BasicProfile via company profile provider
    const isLogoAvailable = logoUrl !== 'logo/null';

    const { isMobile } = useDeviceSize();

    const urlMoreAdsSameOrganization = leisure
        ? `/realestate/leisuresale/search.html?orgId=${orgId}`
        : `/realestate/newbuildings/search.html?orgId=${orgId}`;

    const urlContactForm = id => `/contact.html?ci=${id}&finnkode=${finnkode}`;

    const isKfs = adType === AD_TYPE_KFS;

    const noImages = () => {
        const brokerImages = !_.isEmpty(brokers) ? brokers.filter(broker => broker.contactPicture !== null) : null;
        return _.isEmpty(brokerImages);
    };

    const fullLogoUrl = url => {
        return `${pictureUrl}logo/${url}`;
    };

    const profilePictureUrl = (url, logo) => {
        if (logo) {
            return `${pictureUrl}/${url}`;
        }
        return `${imageHost}/dynamic/220x220c/${url}`;
    };

    const fixColor = color => {
        return color && !color.startsWith('#') ? `#${color}` : color;
    };

    const externalLink = (url, title, icon, trackEvent) => {
        return (
            !_.isEmpty(url) && (
                <S.LinkColor
                    textColor={fixColor(mainTextColor)}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer external nofollow"
                    className="flex align-middle justify-between"
                    onClick={trackEvent}
                >
                    {title} {icon && <LinkIcon />}
                </S.LinkColor>
            )
        );
    };

    const internalLink = (url, title) => {
        return (
            !_.isEmpty(url) && (
                <S.LinkColor textColor={fixColor(mainTextColor)} href={url}>
                    {title}
                </S.LinkColor>
            )
        );
    };

    const messageButton = (email, index) => {
        return (
            !_.isEmpty(email) && (
                <S.LinkColor
                    textColor={fixColor(mainTextColor)}
                    href={urlContactForm(index)}
                    target="_blank"
                    rel="noopener noreferrer external nofollow"
                    onClick={() => {
                        trackSendMessage(index);
                    }}
                >
                    Send melding
                </S.LinkColor>
            )
        );
    };

    const renderContact = (picture, name, title, phone, website, email, index, developerContact, financialAgent) => {
        return (
            <div key={`contact-${index}`}>
                {developerContact && <h3 className="mb-16">Utbygger</h3>}
                <S.Contact developerContact={developerContact} noImages={noImages()} isMobile={isMobile}>
                    <S.ProfileImageWrapper
                        hasImage={!_.isEmpty(picture)}
                        developerContact={developerContact}
                        financialAgent={financialAgent}
                        isMobile={isMobile}
                        noImages={noImages()}
                    >
                        {!_.isEmpty(picture) && (
                            <S.ProfileImage
                                src={profilePictureUrl(picture.path, developerContact)}
                                alt={picture.caption}
                                developerContact={developerContact}
                            />
                        )}
                    </S.ProfileImageWrapper>

                    <div className="break-words">
                        <div className="mb-8">
                            {name && <p className="h4">{name}</p>}
                            {title && <p>{title}</p>}
                        </div>

                        {(!_.isEmpty(website) || !_.isEmpty(phone) || !_.isEmpty(email)) && (
                            <div className="underline mt-16 whitespace-nowrap">
                                {externalLink(website, 'Hjemmeside')}
                                <span className="flex flex-wrap gap-y-8 gap-x-16 mt-8">
                                    {externalLink(
                                        !_.isEmpty(phone) && `tel:${phone}`,
                                        formatPhoneNumber(phone),
                                        null,
                                        () => {
                                            trackClickOnCallContact(expandable ? 'Header' : 'Footer');
                                        }
                                    )}
                                    {messageButton(email, index)}
                                </span>
                            </div>
                        )}
                    </div>
                </S.Contact>
            </div>
        );
    };

    const renderLinks = () => {
        return (
            <>
                {projectUrl && (
                    <p>
                        {externalLink(projectUrl, 'Se prosjektets hjemmeside', true, () => {
                            eventCollectorProjectUrlClick();
                            trackGoToHomepage();
                        })}
                    </p>
                )}
                {orderUrl && (
                    <p>
                        {externalLink(orderUrl, 'Se komplett salgsoppgave', true, () => {
                            eventCollectorProspectUrlClick();
                            trackEventOrderProspect();
                        })}
                    </p>
                )}
                {moreInfoLinks &&
                    moreInfoLinks.length > 0 &&
                    moreInfoLinks
                        .slice(0, 4)
                        .map((link, index) => (
                            <p key={`moreInfoLink-orgProfile-${index}`}>
                                {externalLink(link.url, link.urlText, true, () =>
                                    trackClickOnLinksInUsefulLinks(expandable ? 'Header' : 'Footer')
                                )}
                            </p>
                        ))}

                {customLinks &&
                    customLinks.length > 0 &&
                    customLinks.map((link, index) => (
                        <p key={`tp-link-${index}`}>
                            {externalLink(link.url, link.text, true, () => trackClickOnExtendedProfileLink(index))}
                        </p>
                    ))}
                <p>{internalLink(urlMoreAdsSameOrganization, 'Flere annonser fra annonsør')}</p>

                {ctaButton && (
                    <S.CtaButton
                        href={ctaButton.url}
                        className="button max-w-full"
                        ctaButtonColor={ctaButtonColor}
                        target="_blank"
                        rel="noopener noreferrer external nofollow"
                        onClick={() => trackClickOnExtendedProfileCta()}
                    >
                        {ctaButton.text}
                    </S.CtaButton>
                )}
            </>
        );
    };

    const renderContacts = () => {
        return (
            <>
                {!_.isEmpty(brokers) &&
                    brokers.map((broker, index) => {
                        return renderContact(
                            broker.contactPicture,
                            broker.contactName,
                            broker.contactTitle,
                            broker.contactTelephoneNumber,
                            broker.contactUrl,
                            broker.contactEmail,
                            index
                        );
                    })}
                {!_.isEmpty(financialAgents) &&
                    renderContact(
                        null,
                        financialAgents[0].name,
                        financialAgents[0].companyName,
                        financialAgents[0].mobile,
                        financialAgents[0].url,
                        financialAgents[0].email,
                        brokers.length + developers.length,
                        false,
                        true
                    )}
                {!_.isEmpty(developers) && (
                    <div>
                        {renderContact(
                            isKfs ? developers[0].developerLogo : developers[0].logo,
                            isKfs ? developers[0].developerCompanyName : developers[0].companyName,
                            null,
                            isKfs ? developers[0].developerTelephoneNumber : developers[0].telephoneNumber,
                            null,
                            isKfs ? developers[0].developerEmail : developers[0].email,
                            brokers.length,
                            true
                        )}
                    </div>
                )}
            </>
        );
    };

    const hasSingleContact = () =>
        (!_.isEmpty(brokers) && brokers.length) + (!_.isEmpty(financialAgents) && financialAgents.length) === 1;

    return (
        <Expandable
            expanded={isExpanded}
            box
            chevron={false}
            buttonClass="px-0 pb-0"
            contentClass="px-0"
            onChange={value => setExpanded(value)}
            animated
            title={
                <div className="w-full text-center">
                    <S.Colors
                        backgroundColor={fixColor(logoBackgroundColor)}
                        minHeight="100px"
                        className={`flex justify-center items-center rounded-t-8 ${
                            slogan || isExpanded ? '' : 'rounded-b-8'
                        }`}
                    >
                        {isLogoAvailable ? (
                            <div className="my-24">
                                <S.Logo src={fullLogoUrl(logoUrl)} alt={orgDisplayName} adType={adType} />
                            </div>
                        ) : (
                            <div className="my-24">
                                <p className="py-16 m-0">{orgDisplayName}</p>
                            </div>
                        )}

                        {expandable && (
                            <S.ArrowDown
                                aria-expanded={expanded}
                                className="button button--pill button--small"
                                onClick={() => setExpanded(!expanded)}
                                isMobile={isMobile}
                            >
                                {expanded ? <MinusIcon /> : <ChevronDown />}
                            </S.ArrowDown>
                        )}
                    </S.Colors>
                    {slogan && (
                        <S.Colors
                            textColor={fixColor(sloganTextColor)}
                            backgroundColor={fixColor(sloganBackgroundColor)}
                            className={isExpanded ? 'mt-2' : 'mt-2 rounded-b-8'}
                        >
                            <p className="py-16 m-0">{slogan}</p>
                        </S.Colors>
                    )}
                </div>
            }
        >
            <S.Colors
                className="mt-2 rounded-b-8"
                textColor={fixColor(mainTextColor)}
                backgroundColor={fixColor(mainBackgroundColor)}
            >
                <div className="flex py-32 justify-center md:justify-between items-center md:items-start gap-32 md:gap-8 text-left px-32 md:px-64 lg:px-112 flex-col md:flex-row">
                    <S.Column isMobile={isMobile}>
                        <h3 className="mb-16">{hasSingleContact() ? 'Kontaktperson' : 'Kontaktpersoner'}</h3>
                        <div className="grid gap-32">{renderContacts(isKfs)}</div>
                    </S.Column>

                    <S.Column isMobile={isMobile}>
                        <h3 className="mb-16">Nyttige lenker</h3>
                        {renderLinks()}
                    </S.Column>
                </div>
            </S.Colors>
        </Expandable>
    );
};

CompanyProfile.propTypes = {
    expandable: PropTypes.bool,
    data: PropTypes.object,
};

export default CompanyProfile;
